import PasswordInput from '../components/PasswordInput';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ログインID変更用のstate
  const [newUsername, setNewUsername] = useState('');
  const [isUsernameLoading, setIsUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');

  // パスワード変更用のstate
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // マスターアカウント以外はアクセス不可
  if (!user || user.role !== 'master') {
    return (
      <div style={{ maxWidth: '800px', margin: '50px auto', padding: '20px' }}>
        <h1>アクセス権限がありません</h1>
        <p>この画面はマスターアカウント専用です。</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ダッシュボードに戻る
        </button>
      </div>
    );
  }

  const handleUsernameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUsernameLoading(true);
    setUsernameError('');
    setUsernameSuccess('');

    // バリデーション
    if (newUsername.length < 3) {
      setUsernameError('ログインIDは3文字以上である必要があります');
      setIsUsernameLoading(false);
      return;
    }

    try {
      const response = await api.put('/auth/change-username', { newUsername });
      setUsernameSuccess(response.data.message);
      setNewUsername('');
      // ユーザー情報を再読み込みするために画面をリロード
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      setUsernameError(err.response?.data?.error || 'ログインID変更に失敗しました');
    } finally {
      setIsUsernameLoading(false);
    }
  };

  const handleRequestEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.post('/auth/request-email-change', { newEmail });
      setSuccessMessage(response.data.message);
      setNewEmail('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'メールアドレス変更リクエストに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // バリデーション
    if (newPassword !== confirmPassword) {
      setPasswordError('新しいパスワードと確認用パスワードが一致しません');
      setIsPasswordLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('パスワードは6文字以上である必要があります');
      setIsPasswordLoading(false);
      return;
    }

    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setPasswordSuccess(response.data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'パスワード変更に失敗しました');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>アカウント設定</h1>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ダッシュボードに戻る
        </button>
      </div>

      {error && (
        <div
          style={{
            color: '#721c24',
            backgroundColor: '#f8d7da',
            padding: '10px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          style={{
            color: '#155724',
            backgroundColor: '#d4edda',
            padding: '10px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
          }}
        >
          {successMessage}
        </div>
      )}

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>現在の情報</h2>
        <div style={{ marginBottom: '10px' }}>
          <strong>ログインID:</strong> {user.username}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>名前:</strong> {user.name}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>メールアドレス:</strong> {user.email}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>役職:</strong> マスターアカウント
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>ログインID変更</h2>

        {usernameError && (
          <div
            style={{
              color: '#721c24',
              backgroundColor: '#f8d7da',
              padding: '10px',
              marginBottom: '15px',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
            }}
          >
            {usernameError}
          </div>
        )}

        {usernameSuccess && (
          <div
            style={{
              color: '#155724',
              backgroundColor: '#d4edda',
              padding: '10px',
              marginBottom: '15px',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
            }}
          >
            {usernameSuccess}
          </div>
        )}

        <form onSubmit={handleUsernameChange}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="newUsername" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              新しいログインID
            </label>
            <input
              type="text"
              id="newUsername"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              required
              minLength={3}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <small style={{ color: '#6c757d', fontSize: '12px' }}>3文字以上の英数字</small>
          </div>
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '15px',
              fontSize: '14px',
              color: '#856404',
            }}
          >
            ⚠️ ログインID変更後、ログイン時には新しいログインIDを使用してください。
          </div>
          <button
            type="submit"
            disabled={isUsernameLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: isUsernameLoading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isUsernameLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isUsernameLoading ? '変更中...' : 'ログインIDを変更'}
          </button>
        </form>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>パスワード変更</h2>

        {passwordError && (
          <div
            style={{
              color: '#721c24',
              backgroundColor: '#f8d7da',
              padding: '10px',
              marginBottom: '15px',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
            }}
          >
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div
            style={{
              color: '#155724',
              backgroundColor: '#d4edda',
              padding: '10px',
              marginBottom: '15px',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
            }}
          >
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              現在のパスワード
            </label>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              新しいパスワード
            </label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
            <small style={{ color: '#6c757d', fontSize: '12px' }}>6文字以上</small>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              新しいパスワード（確認）
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={isPasswordLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: isPasswordLoading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isPasswordLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isPasswordLoading ? '変更中...' : 'パスワードを変更'}
          </button>
        </form>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>メールアドレス変更</h2>
        <form onSubmit={handleRequestEmailChange}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="newEmail" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              新しいメールアドレス
            </label>
            <input
              type="email"
              id="newEmail"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '15px',
              fontSize: '14px',
              color: '#856404',
            }}
          >
            ⚠️ 新しいメールアドレスに確認メールが送信されます。メール内のリンクをクリックして変更を完了してください。
          </div>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: isLoading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isLoading ? '送信中...' : '変更リクエストを送信'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
